import React, { useState, Component } from 'react';
import { NavigationProps } from '../types/navigation';
import { Button } from '../components/ui/Button';
import { Share2, Copy, Users, Check, Lock, ShoppingBag, Star } from 'lucide-react';
import { useStore, normalizePhone, hasUnlockedFriendInvites } from '../lib/store';
import { InstagramFollow } from '../components/InstagramFollow';
export function FriendsPage({
  onNavigate
}: NavigationProps) {
  const {
    user,
    registeredUsers,
    reservations
  } = useStore();
  const [copied, setCopied] = useState(false);
  const normalizedPhone = user?.phone ? normalizePhone(user.phone) : '';
  const shareUrl = `${window.location.origin}?ref=${encodeURIComponent(normalizedPhone)}`;
  const invitedFriends = registeredUsers.filter(u => u.invitedBy === normalizedPhone);
  // Invitations unlock only after the user has redeemed AND reviewed a catch.
  const invitesUnlocked = hasUnlockedFriendInvites(reservations);
  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Finns i Sjön',
          text: '🎣 Join the crew and get your first catch!',
          url: shareUrl
        });
        return;
      }
    } catch {
      // fallback
    }
    handleCopy();
  };
  const handleCopy = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return <div className="min-h-screen bg-brand-ice pb-32" data-id="element-2066" data-mp="rlw48o1m42m4">
      <div className="bg-brand-purple pt-7 pb-16 px-6 text-center rounded-b-[2.5rem] relative overflow-hidden shadow-[0_16px_34px_rgba(96,70,232,0.22)]" data-id="element-2067" data-mp="wd4d9iz15lyr">
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]" data-id="element-2068" data-mp="inkh4vzyfmyf"></div>
        <div className="relative z-10" data-id="element-2069" data-mp="ml8cg05k7hqu">
          <div className="w-20 h-20 bg-white/95 rounded-full flex items-center justify-center mx-auto mb-4 shadow-[0_0_30px_rgba(255,255,255,0.18)]" data-id="element-2070" data-mp="8njtixv1ykof">
            <Users className="w-10 h-10 text-brand-purple" data-id="element-2071" data-mp="dozhoysj17b1" />
          </div>
          <h1 className="text-4xl font-display text-white tracking-tight mb-2" data-id="element-2072" data-mp="2fdn2zpvdbwkw">
            Catch with Friends
          </h1>
          <p className="text-white/85 max-w-xs mx-auto font-medium" data-id="element-2073" data-mp="2ajixlb1r3wlx">
            Invite friends to join the crew. You get{' '}
            <span className="font-bold text-brand-yellow" data-id="element-2074" data-mp="21l7a3hw6lexp">+40 points</span> when
            they redeem their first catch!
          </p>
        </div>
      </div>

      <div className="px-6 -mt-8" data-id="element-2075" data-mp="2y8u0oz6kq338">
        {invitesUnlocked ? <div className="bg-white rounded-[1.75rem] p-6 shadow-[0_16px_38px_rgba(22,38,95,0.12)] space-y-6 border border-white" data-id="element-2076" data-mp="2uany83crq9o4">
            <div className="text-center" data-id="element-2077" data-mp="2pccp5uuas36y">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2" data-id="element-2078" data-mp="2kbooehg9o7zj">
                Your Invite Link
              </p>
              <div className="bg-brand-ice p-3 rounded-2xl flex items-center justify-between border border-brand-lavender overflow-hidden" data-id="element-2079" data-mp="1dwea9acfumxt">
                <code className="text-brand-purple font-mono text-sm truncate mr-2" data-id="element-2080" data-mp="ysbgs6f7xnko">
                  {shareUrl}
                </code>
                <button onClick={handleCopy} aria-label="Copy invite link" className="p-2 hover:bg-white rounded-lg transition-colors shrink-0" data-id="element-2081" data-mp="13rxvkkap3bvl">
                  {copied ? <Check className="w-4 h-4 text-green-500" data-id="element-2082" data-mp="1hikuodkdb3s7" /> : <Copy className="w-4 h-4 text-gray-500" data-id="element-2083" data-mp="1hkx9ihutwy11" />}
                </button>
              </div>
            </div>

            <Button className="w-full" onClick={handleShare} data-id="element-2084" data-mp="1rgh2ave3jglk">
              <Share2 className="w-4 h-4 mr-2" data-id="element-2085" data-mp="1rewahdj62bre" />
              Share Link
            </Button>
          </div> : (/* Locked — invitations open after a first redemption + review. */
      <div className="bg-white rounded-[1.75rem] p-6 shadow-[0_16px_38px_rgba(22,38,95,0.12)] text-center border border-white" data-id="element-2086" data-mp="3tagah7vx9hu1">
            <div className="w-16 h-16 bg-brand-lavender rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2087" data-mp="1si4swj58pfvu">
              <Lock className="w-7 h-7 text-brand-purple" data-id="element-2088" data-mp="1nj1l4qxvkvk6" />
            </div>
            <h2 className="text-lg font-bold text-gray-900 mb-1" data-id="element-2089" data-mp="21k46t2kyk3uk">
              Invites unlock after your first catch
            </h2>
            <p className="text-sm text-gray-500 mb-5 max-w-xs mx-auto" data-id="element-2090" data-mp="1wl12jkcsy1nn">
              Redeem a catch and leave a review, then you can invite friends and
              earn <span className="font-bold text-brand-blue" data-id="element-2091" data-mp="14tva7q3bp88x">+40 points</span>{' '}
              for each one who joins.
            </p>

            <ol className="text-left space-y-3 mb-6" data-id="element-2092" data-mp="1imb6m81py80v">
              <li className="flex items-center gap-3" data-id="element-2093" data-mp="1du9xuw3rezfl">
                <span className="w-9 h-9 rounded-full bg-brand-blue/10 flex items-center justify-center shrink-0" data-id="element-2094" data-mp="1dsp2j49mfcgo">
                  <ShoppingBag className="w-4 h-4 text-brand-blue" data-id="element-2095" data-mp="q22v9f7fxw7q" />
                </span>
                <span className="text-sm font-medium text-gray-700" data-id="element-2096" data-mp="l39tgk1intne">
                  1. Redeem your first catch
                </span>
              </li>
              <li className="flex items-center gap-3" data-id="element-2097" data-mp="31jobxd0di4xm">
                <span className="w-9 h-9 rounded-full bg-brand-yellow/20 flex items-center justify-center shrink-0" data-id="element-2098" data-mp="31l95p0ev5juf">
                  <Star className="w-4 h-4 text-brand-blue" data-id="element-2099" data-mp="2squp8uilk39q" />
                </span>
                <span className="text-sm font-medium text-gray-700" data-id="element-2100" data-mp="2soqg9y5uce2l">
                  2. Review it to unlock invites
                </span>
              </li>
            </ol>

            <Button variant="secondary" className="w-full" onClick={() => onNavigate('catches')} data-id="element-2101" data-mp="3khha6201xqoo">
              <ShoppingBag className="w-4 h-4 mr-2" data-id="element-2102" data-mp="3bgsy3cu7y0ef" />
              Go to My Catches
            </Button>
          </div>)}

        {/* Keep in touch — follow us on Instagram for new drops */}
        <InstagramFollow className="mt-6" data-id="element-2103" data-mp="202p5ob3a4kbh" />

        <div className="mt-8" data-id="element-2104" data-mp="203qiajzy1evb">
          <h3 className="font-display text-2xl text-brand-navy mb-4" data-id="element-2105" data-mp="1g02pv5nk075g">
            Your Crew <span className="text-brand-purple" data-id="element-2106" data-mp="1g25ebl925s82">({invitedFriends.length})</span>
          </h3>
          {invitedFriends.length > 0 ? <div className="space-y-3" data-id="element-2107" data-mp="1777hi32shvbg">
              {invitedFriends.map((friend, i) => <div key={i} className="bg-white rounded-2xl p-4 flex items-center justify-between shadow-[0_8px_22px_rgba(22,38,95,0.06)] border border-white" data-id="element-2108" data-mp="129fsphxfypbh" data-mpi={i}>
                  <div className="flex items-center space-x-3" data-id="element-2109" data-mp="s9ovh8hhxw7p">
                    <div className="w-11 h-11 bg-brand-lavender rounded-full flex items-center justify-center text-brand-purple font-bold" data-id="element-2110" data-mp="o950obkeb4oq">
                      {friend.name.charAt(0).toUpperCase()}
                    </div>
                    <div data-id="element-2111" data-mp="2infpik0zpyui">
                      <p className="font-bold text-gray-900" data-id="element-2112" data-mp="2imnrz3nu1r1l">{friend.name}</p>
                      <p className="text-xs text-gray-500" data-id="element-2113" data-mp="2dp4ei5yyliap">
                        Joined via your link
                      </p>
                    </div>
                  </div>
                  <div className="bg-brand-yellow/30 text-brand-navy text-xs font-bold px-3 py-1.5 rounded-full" data-id="element-2114" data-mp="2q65e8osgov1u">
                    +40 pts
                  </div>
                </div>)}
            </div> : <div className="bg-white rounded-xl p-6 text-center text-gray-500 border border-gray-100 border-dashed" data-id="element-2115" data-mp="2zv6w6kikhmig">
              <p data-id="element-2116" data-mp="22akj0rda6wzq">No friends yet.</p>
              <p className="text-sm mt-1" data-id="element-2117" data-mp="277kp32a4rzqu">Start sharing to earn badges!</p>
            </div>}
        </div>
      </div>
    </div>;
}